import {
  SelectedProposalPackage,
  ServiceProposalPackage,
  ServiceProposalPartAction,
} from "@repo/common"
import { ServiceProposalIconDescriptions } from "@repo/ui/components/service-proposal-icon-descriptions"
import { Button } from "@repo/ui/components/ui/button"
import { addPackage, removePackage } from "@repo/ui/lib/product-choice-utils"
import { SelectableCardLabel } from "@repo/ui/tanstack-form"
import { ChevronRight } from "lucide-react"
import { useTranslations } from "use-intl"
import { useFieldContext } from "../hooks/form-contexts"
import { ServiceProposalPackageCard } from "./service-proposal-package-card"

export interface ServiceProposalPackageFieldProps {
  availablePackages: ServiceProposalPackage[]
  standalonePartActions: ServiceProposalPartAction[]
  onEditPackage?: (originalPackageId: string) => void
  onPackageDeselected?: (packageData: ServiceProposalPackage) => void
  onPackageSelected?: (packageData: ServiceProposalPackage) => void
}

export const ServiceProposalPackageField = ({
  availablePackages,
  standalonePartActions,
  onEditPackage,
  onPackageDeselected,
  onPackageSelected,
}: ServiceProposalPackageFieldProps) => {
  const t = useTranslations("ui")
  const field = useFieldContext<SelectedProposalPackage[]>()

  const numComments = field.state.value.reduce((total, selectedPackage) => {
    const packageData = availablePackages.find(
      (pkg) => pkg.originalPackageId === selectedPackage.originalPackageId,
    )
    if (!packageData) return total

    return (
      total +
      packageData.partActions.reduce((actionTotal, action) => {
        return actionTotal + (action.comment ? 1 : 0)
      }, 0)
    )
  }, 0)

  const numProductChoices = field.state.value.reduce(
    (total, selectedPackage) => {
      const packageData = availablePackages.find(
        (pkg) => pkg.originalPackageId === selectedPackage.originalPackageId,
      )
      if (!packageData) return total

      return (
        total +
        packageData.partActions.reduce((actionTotal, action) => {
          return actionTotal + action.productChoices.length
        }, 0)
      )
    },
    0,
  )

  const numProducts = field.state.value.reduce((total, selectedPackage) => {
    const packageData = availablePackages.find(
      (pkg) => pkg.originalPackageId === selectedPackage.originalPackageId,
    )
    if (!packageData) return total

    return (
      total +
      packageData.partActions.reduce((actionTotal, action) => {
        return actionTotal + action.products.length
      }, 0)
    )
  }, 0)

  return (
    <div className="space-y-4">
      <SelectableCardLabel className="text-secondary-foreground">
        {t("readOnlyServiceProposal.packages")}
      </SelectableCardLabel>

      <div className="space-y-3">
        {availablePackages.map((packageData) => {
          const selectedPackage = field.state.value.find(
            (sp) => sp.originalPackageId === packageData.originalPackageId,
          )
          const isSelected = !!selectedPackage
          const isPackageLocked = packageData.isApprovedViaReception

          return (
            <div key={packageData.snapshotPackageId} className="space-y-2">
              <div className="flex gap-3">
                <div className="flex-1">
                  <ServiceProposalPackageCard
                    data={packageData}
                    selectedPackage={selectedPackage}
                    selected={isSelected}
                    standalonePartActions={standalonePartActions}
                    interactive={!isPackageLocked}
                    isLocked={isPackageLocked}
                    onSelect={() => {
                      if (isPackageLocked) return
                      if (isSelected) {
                        field.handleChange(
                          removePackage(
                            field.state.value,
                            packageData.originalPackageId,
                          ),
                        )
                        onPackageDeselected?.(packageData)
                      } else {
                        field.handleChange(
                          addPackage(field.state.value, packageData),
                        )
                        onPackageSelected?.(packageData)
                      }
                    }}
                  />
                </div>

                <Button
                  type="button"
                  disabled={!selectedPackage}
                  aria-label={t("proposalForm.editPackage", {
                    name: packageData.name,
                  })}
                  className="self-stretch h-auto flex items-center justify-center px-5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                  onClick={() => onEditPackage?.(packageData.originalPackageId)}
                >
                  <ChevronRight className="min-w-6 min-h-6 text-secondary-foreground" />
                </Button>
              </div>
            </div>
          )
        })}

        <ServiceProposalIconDescriptions
          showTotalProducts={numProducts > 0}
          showComments={numComments > 0}
          showProductChoices={numProductChoices > 0}
        />
      </div>
    </div>
  )
}
